/**
 * Service for managing NoteType API operations
 */

import { BaseService } from './BaseService';

/**
 * NoteType Service Class
 */
class NoteTypeService extends BaseService {
    constructor() {
        super('/api/v1/note-types');
    }

    /**
     * Validate note type data
     * @param {Object} data - Note type data
     */
    validateNoteTypeData(data) {
        if (!data.noteTypeName || typeof data.noteTypeName !== 'string') {
            throw new Error('Note type name is required and must be a string');
        }
        
        if (data.noteTypeName.trim().length === 0) {
            throw new Error('Note type name cannot be empty');
        }
        
        if (data.noteTypeName.length > 50) {
            throw new Error('Note type name cannot exceed 50 characters');
        }
        
        if (data.noteTypeDesc && data.noteTypeDesc.length > 500) {
            throw new Error('Note type description cannot exceed 500 characters');
        }

        // Check that at least one visibility flag is true
        const hasVisibility = 
            data.visibleClientServices || 
            data.visibleBilling || 
            data.visibleSampleControl || 
            data.visibleLaboratory || 
            data.visibleQA || 
            data.visibleClient;

        if (!hasVisibility) {
            throw new Error('At least one module must be visible for the note type');
        }
    }
    /**
     * Get all note types
     * @param {string} accessToken - JWT access token
     * @param {boolean} activeOnly - If true, returns only active note types
     * @returns {Promise<Array>} Array of NoteType objects
     */
    async getAll(accessToken, activeOnly = false) {
        try {
            const endpoint = activeOnly ? '?active=true' : '';
            return await this.makeRequest(accessToken, endpoint);
        } catch (error) {
            console.error('Failed to fetch note types:', error);
            throw error;
        }
    }

    /**
     * Get note type by ID - uses BaseService implementation
     */
    // Inherited from BaseService

    /**
     * Get note types by module
     * @param {string} accessToken - JWT access token
     * @param {string} module - Module name (ClientServices, Billing, SampleControl, Laboratory, QA, Client)
     * @returns {Promise<Array>} Array of NoteType objects
     */
    async getByModule(accessToken, module) {
        try {
            return await this.makeRequest(accessToken, `/module/${module}`);
        } catch (error) {
            console.error(`Failed to fetch note types for module ${module}:`, error);
            throw error;
        }
    }

    /**
     * Get note types for Client Services module
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of NoteType objects
     */
    async getClientServices(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/client-services');
        } catch (error) {
            console.error('Failed to fetch client services note types:', error);
            throw error;
        }
    }

    /**
     * Get note types for Billing module
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of NoteType objects
     */
    async getBilling(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/billing');
        } catch (error) {
            console.error('Failed to fetch billing note types:', error);
            throw error;
        }
    }

    /**
     * Get note types for Sample Control module
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of NoteType objects
     */
    async getSampleControl(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/sample-control');
        } catch (error) {
            console.error('Failed to fetch sample control note types:', error);
            throw error;
        }
    }

    /**
     * Get note types for Laboratory module
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of NoteType objects
     */
    async getLaboratory(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/laboratory');
        } catch (error) {
            console.error('Failed to fetch laboratory note types:', error);
            throw error;
        }
    }

    /**
     * Get note types for QA module
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of NoteType objects
     */
    async getQA(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/qa');
        } catch (error) {
            console.error('Failed to fetch QA note types:', error);
            throw error;
        }
    }

    /**
     * Get note types for Client module
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Array>} Array of NoteType objects
     */
    async getClient(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/client');
        } catch (error) {
            console.error('Failed to fetch client note types:', error);
            throw error;
        }
    }

    /**
     * Create a new note type with validation
     * @param {string} accessToken - JWT access token
     * @param {Object} noteType - NoteType object without ID
     * @returns {Promise<Object>} Created NoteType object
     */
    async create(accessToken, noteType) {
        this.validateNoteTypeData(noteType);
        return super.create(accessToken, noteType);
    }

    /**
     * Update an existing note type with validation
     * @param {string} accessToken - JWT access token
     * @param {number} id - Note type ID
     * @param {Object} noteType - Updated NoteType object
     * @returns {Promise<Object>} Updated NoteType object
     */
    async update(accessToken, id, noteType) {
        this.validateNoteTypeData(noteType);
        return super.update(accessToken, id, noteType);
    }

    /**
     * Delete a note type - uses BaseService implementation
     */
    // Inherited from BaseService

    /**
     * Enable note type for a specific module
     * @param {string} accessToken - JWT access token
     * @param {number} id - Note type ID
     * @param {string} module - Module name
     * @returns {Promise<Object>} Success response
     */
    async enableModule(accessToken, id, module) {
        try {
            return await this.makeRequest(accessToken, `/${id}/enable/${module}`, {
                method: 'PATCH'
            });
        } catch (error) {
            console.error(`Failed to enable module ${module} for note type ${id}:`, error);
            throw error;
        }
    }

    /**
     * Disable note type for a specific module
     * @param {string} accessToken - JWT access token
     * @param {number} id - Note type ID
     * @param {string} module - Module name
     * @returns {Promise<Object>} Success response
     */
    async disableModule(accessToken, id, module) {
        try {
            return await this.makeRequest(accessToken, `/${id}/disable/${module}`, {
                method: 'PATCH'
            });
        } catch (error) {
            console.error(`Failed to disable module ${module} for note type ${id}:`, error);
            throw error;
        }
    }

    /**
     * Update note visibility for a note type
     * @param {string} accessToken - JWT access token
     * @param {number} id - Note type ID
     * @param {Object} modules - Object with module visibility flags
     * @returns {Promise<Object>} Success response
     */
    async updateModuleAvailability(accessToken, id, modules) {
        try {
            return await this.makeRequest(accessToken, `/${id}/modules`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(modules)
            });
        } catch (error) {
            console.error(`Failed to update module visibility for note type ${id}:`, error);
            throw error;
        }
    }

    /**
     * Get module statistics
     * @param {string} accessToken - JWT access token
     * @returns {Promise<Object>} ModuleStatistics object
     */
    async getStatistics(accessToken) {
        try {
            return await this.makeRequest(accessToken, '/statistics');
        } catch (error) {
            console.error('Failed to fetch note type statistics:', error);
            throw error;
        }
    }

    /**
     * Search note types by module criteria
     * @param {string} accessToken - JWT access token
     * @param {Object} criteria - Object with module criteria
     * @returns {Promise<Array>} Array of NoteType objects
     */
    async search(accessToken, criteria) {
        try {
            return await this.makeRequest(accessToken, '/search', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(criteria)
            });
        } catch (error) {
            console.error('Failed to search note types:', error);
            throw error;
        }
    }
}

// Export singleton instance
export const noteTypeService = new NoteTypeService();

// Export individual methods for convenience
export const {
    getAll: getAllNoteTypes,
    getById: getNoteTypeById,
    getByModule: getNoteTypesByModule,
    getClientServices: getClientServicesNoteTypes,
    getBilling: getBillingNoteTypes,
    getSampleControl: getSampleControlNoteTypes,
    getLaboratory: getLaboratoryNoteTypes,
    getQA: getQANoteTypes,
    getClient: getClientNoteTypes,
    create: createNoteType,
    update: updateNoteType,
    delete: deleteNoteType,
    enableModule: enableNoteTypeModule,
    disableModule: disableNoteTypeModule,
    updateModuleAvailability: updateNoteTypeModuleAvailability,
    getStatistics: getNoteTypeStatistics,
    search: searchNoteTypes
} = noteTypeService;

// Valid module names constant
export const VALID_MODULES = [
    'ClientServices',
    'Billing',
    'SampleControl',
    'Laboratory',
    'QA',
    'Client'
];

// Module display names for UI
export const MODULE_DISPLAY_NAMES = {
    ClientServices: 'Client Services',
    Billing: 'Billing',
    SampleControl: 'Sample Control',
    Laboratory: 'Laboratory',
    QA: 'QA',
    Client: 'Client'
};

export default noteTypeService;
